import HistoryIcon from '@/Components/Common/Icons/HistoryIcon.svg';
import InfoIcon from '@/Components/Common/Icons/InformationIcon.svg';
import useLocationTranslation from '@/Components/Hooks/useLocationTranslation';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { Alert, ConfigProvider, Row, Spin, Table } from 'antd';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { ALERT_HISTORY_DAYS, useAlertEventsStream } from '../hooks';
import { alertColumn, formatTableData } from '../utils';

const INITIAL_VISIBLE_EVENTS = 80;
const LOAD_MORE_EVENTS = 40;
const SCROLL_LOAD_THRESHOLD = 80;

const NoAlertsDiv = styled.div`
  &.alerts-table-container {
    display: flex;
    align-items: center;
    padding-left: 30px;
    width: 100%;
    height: 80px;
    border-radius: 10px;
    background: #ffffff;
    box-shadow:
      0 2px 8px rgba(0, 0, 0, 0.06),
      0 12px 32px rgba(0, 0, 0, 0.08);
    .no-alerts-icon {
      width: 20px;
      height: 20px;
      margin-right: 10px;
    }
    .no-alerts {
      font-size: 15px;
      font-weight: 500;
      color: #0a0a0a;
    }
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  padding: 40px;
  background: #ffffff;
  border-radius: 10px;
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.06),
    0 12px 32px rgba(0, 0, 0, 0.08);

  .table-title {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    .history-icon {
      width: 26px;
      height: 26px;
      margin-right: 10px;
    }
    .title-text {
      font-size: 22px;
      font-weight: 900;
      color: #005d98;
    }
    .stream-spinner {
      margin-left: 12px;
    }
  }

  .ant-table-wrapper {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }
`;

const AlertTable = ({
  useruuid,
  hc200DeviceUuid,
  timezone,
  locale,
  is24hours,
  userAccessories,
  supportedCategories,
}) => {
  const [visibleEventCount, setVisibleEventCount] = useState(INITIAL_VISIBLE_EVENTS);
  const [prevScope, setPrevScope] = useState({ useruuid, hc200DeviceUuid, supportedCategories });
  if (
    prevScope.useruuid !== useruuid ||
    prevScope.hc200DeviceUuid !== hc200DeviceUuid ||
    prevScope.supportedCategories !== supportedCategories
  ) {
    setPrevScope({ useruuid, hc200DeviceUuid, supportedCategories });
    setVisibleEventCount(INITIAL_VISIBLE_EVENTS);
  }

  const tableWrapperRef = useRef(null);
  const trans = useSelector(translator);
  const { translateLocation } = useLocationTranslation(useruuid);
  const columns = useMemo(() => alertColumn(trans), [trans]);

  // Rows arrive progressively, already de-duplicated and sorted newest first.
  const {
    data: sortedAlerts,
    isLoading: alertsLoading,
    isStreaming,
    isPartial,
  } = useAlertEventsStream({
    useruuid,
    hc200DeviceUuid,
    supportedCategories,
    days: ALERT_HISTORY_DAYS,
  });

  const visibleTableData = useMemo(() => {
    if (!sortedAlerts.length) return [];
    const currentBatch = sortedAlerts.slice(0, visibleEventCount);
    return formatTableData(
      currentBatch,
      timezone,
      locale,
      trans,
      is24hours,
      userAccessories,
      translateLocation
    );
  }, [
    sortedAlerts,
    visibleEventCount,
    timezone,
    locale,
    trans,
    is24hours,
    userAccessories,
    translateLocation,
  ]);

  useEffect(() => {
    const scrollContainer = tableWrapperRef.current?.querySelector('.ant-table-body');
    if (!scrollContainer) return;

    const handleScroll = () => {
      const remainingScroll =
        scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight;

      if (remainingScroll <= SCROLL_LOAD_THRESHOLD && visibleEventCount < sortedAlerts.length) {
        setVisibleEventCount((currentCount) =>
          Math.min(currentCount + LOAD_MORE_EVENTS, sortedAlerts.length)
        );
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [sortedAlerts.length, visibleEventCount]);

  if (alertsLoading) {
    return (
      <TableWrapper ref={tableWrapperRef}>
        <div className="table-title">
          <HistoryIcon className="history-icon" />
          <h1 className="title-text">{trans('HISTORY')}</h1>
        </div>
        <Row style={{ justifyContent: 'center', padding: '60px 0' }}>
          <Spin spinning={true} size="large" />
        </Row>
      </TableWrapper>
    );
  }

  if (isEmpty(sortedAlerts)) {
    return (
      <NoAlertsDiv className="alerts-table-container">
        <InfoIcon className="no-alerts-icon" />
        <span className="no-alerts">{trans('NO_ALERTS_TRIGGERED')}</span>
      </NoAlertsDiv>
    );
  }

  return (
    <TableWrapper ref={tableWrapperRef}>
      <div className="table-title">
        <HistoryIcon className="history-icon" />
        <h1 className="title-text">{trans('HISTORY')}</h1>
        {isStreaming && <Spin size="small" className="stream-spinner" />}
      </div>
      <ConfigProvider
        theme={{
          components: {
            Table: {
              headerSplitColor: 'transparent',
              cellPaddingBlock: 24,
            },
          },
        }}
      >
        <Table
          size="small"
          columns={columns}
          dataSource={visibleTableData}
          pagination={false}
          scroll={{ y: 550 }}
          components={{
            header: {
              cell: (props) => (
                <th
                  {...props}
                  style={{
                    textAlign: 'left',
                    backgroundColor: '#ffffff',
                    color: '#808080',
                    fontWeight: 600,
                    paddingTop: 18,
                    paddingBottom: 18,
                  }}
                />
              ),
            },
            body: {
              cell: (props) => (
                <td {...props} style={{ verticalAlign: 'middle', fontWeight: 500 }} />
              ),
            },
          }}
        />
      </ConfigProvider>
      {/* The download failed part-way: the alerts above are incomplete. */}
      {isPartial && <Alert type="warning" showIcon message={trans('ERROR_MESSAGE')} />}
    </TableWrapper>
  );
};

AlertTable.propTypes = {
  data: PropTypes.array,
  useruuid: PropTypes.string.isRequired,
  hc200DeviceUuid: PropTypes.string.isRequired,
  timezone: PropTypes.string,
  locale: PropTypes.string,
  is24hours: PropTypes.bool,
  userAccessories: PropTypes.array,
  supportedCategories: PropTypes.array,
};

export default AlertTable;
